import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { user } from "localStorage";
import { hasPageAccess } from "utils/pagePermissions";
import { refreshUserProfile } from "utils/refreshUserProfile";

/**
 * Wraps a route element and redirects to /scan if the current user does not
 * have access to the given page slug. admin role always passes.
 *
 * The "allowed" verdict renders immediately from the cached profile — no
 * extra round trip, since a stale ALLOW that's actually since been revoked is
 * still caught by the backend's own checkPermission on the first API call
 * (that's the real security boundary, this is just UI gating). A "denied"
 * verdict is re-checked against a fresh /user/me/profile read (see
 * utils/refreshUserProfile.js) before committing to the redirect, so
 * re-granting a page slug takes effect immediately instead of requiring a
 * full re-login.
 */
const PermittedRoute = ({ page, element }) => {
  const _user = user();
  const staleAllowed = hasPageAccess(_user, page);
  const [recheck, setRecheck] = useState(undefined); // undefined | "allow" | "deny"

  useEffect(() => {
    if (!_user || staleAllowed) return;
    let active = true;
    refreshUserProfile().then((fresh) => {
      if (!active) return;
      setRecheck(hasPageAccess(fresh, page) ? "allow" : "deny");
    });
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, staleAllowed]);

  if (!_user) return <Navigate to="/admin/login" replace />;
  if (staleAllowed || recheck === "allow") return element;
  if (recheck === "deny") return <Navigate to="/scan" replace />;
  return null; // brief re-check in flight before committing to a redirect
};

export default PermittedRoute;
